import React, {useState, useRef} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faUpload, faTimes, faImage} from '@fortawesome/free-solid-svg-icons';
import {UploadService} from '../../../Services/Upload/UploadService';

interface FileUploadProps {
    value?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    accept?: string;
    maxSize?: number; // in MB
    className?: string;
}

const FileUpload: React.FC<FileUploadProps> =
    ({
         value,
         onChange,
         placeholder = "Selecteer of sleep een bestand",
         accept = "image/*",
         maxSize = 5,
         className = ""
     }) => {
        const [isUploading, setIsUploading] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [previewUrl, setPreviewUrl] = useState<string>('');
        const fileInputRef = useRef<HTMLInputElement>(null);
        const uploadService = UploadService.getInstance();

        const handleFileSelect = async (file: File) => {
            setError(null);

            if (!uploadService.isValidFileSize(file, maxSize)) {
                setError(`Bestandsgrootte mag niet groter zijn dan ${maxSize}MB`);
                return;
            }

            if (accept === "image/*" && !uploadService.isValidImageType(file)) {
                setError('Alleen afbeeldingsbestanden worden geaccepteerd (JPEG, PNG, GIF, WebP)');
                return;
            }

            try {
                setIsUploading(true);
                const response = await uploadService.uploadImage(file);
                onChange(response.url);
                setPreviewUrl(response.publicUrl);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Er is een fout opgetreden');
            } finally {
                setIsUploading(false);
            }
        };

        const handleDrop = (e: React.DragEvent) => {
            e.preventDefault();
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                handleFileSelect(files[0]);
            }
        };

        const handleDragOver = (e: React.DragEvent) => {
            e.preventDefault();
        };

        const handleClick = () => {
            fileInputRef.current?.click();
        };

        const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files && files.length > 0) {
                handleFileSelect(files[0]);
            }
        };

        const handleRemove = () => {
            onChange('');
            setPreviewUrl('');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };

        return (
            <div className={`file-upload ${className}`}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileInputChange}
                    style={{display: 'none'}}
                />

                {value ? (
                    <div className="space-y-3">
                        <div className="relative">
                            <img
                                src={previewUrl || value}
                                alt="Geüploade afbeelding"
                                className="w-full h-48 object-cover rounded-md border shadow-sm"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fallbackElement = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fallbackElement) {
                                        fallbackElement.style.display = 'flex';
                                    }
                                }}
                            />
                            <div
                                className="w-full h-48 bg-gray-100 rounded-md border flex items-center justify-center hidden"
                                style={{display: 'none'}}
                            >
                                <div className="text-center">
                                    <FontAwesomeIcon icon={faImage} className="text-gray-400 text-3xl mb-2"/>
                                    <p className="text-sm text-gray-500">Geen voorbeeld beschikbaar</p>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={handleRemove}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                                title="Verwijderen"
                            >
                                <FontAwesomeIcon icon={faTimes} size="xs"/>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div
                        className={`
            border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors
            ${isUploading ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
                        onClick={!isUploading ? handleClick : undefined}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                    >
                        {isUploading ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <span className="ml-3 text-gray-600">Bezig met uploaden...</span>
                            </div>
                        ) : (
                            <>
                                <FontAwesomeIcon
                                    icon={faUpload}
                                    className="text-gray-400 text-2xl mb-2"
                                />
                                <p className="text-sm text-gray-600 mb-1">{placeholder}</p>
                                <p className="text-xs text-gray-500">
                                    Maximale grootte: {maxSize}MB
                                </p>
                            </>
                        )}
                    </div>
                )}

                {error && (
                    <p className="text-red-500 text-sm mt-2">{error}</p>
                )}
            </div>
        );
    };

export default FileUpload;
